import { parsePath } from "react-router-dom";

/* A client's branded link (e.g. "apc") doubles as their portal URL prefix: /apc, /apc/jobs, /apc/invoices/123 …
   Internally the app keeps routing on /portal/*; SlugRouter maps between the two, so portal pages never need to know. */
const KEY = "portal_slug";
const SLUG_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;
const listeners = new Set();

// Top-level paths the app itself owns — never treated as a client slug (mirrors RESERVED_SLUGS on the backend)
export const RESERVED_PATHS = new Set(["admin", "api", "portal", "login", "necos", "necosfm", "supplier", "vendor", "reset-password", "apply", "static", "assets",
  "accept-invite", "activity-log", "admin-messages", "ai-assistant", "best-supplier", "calendar", "change-password", "client-approval", "client-dashboard",
  "client-portal", "clients", "compliance-hub", "contracts", "dispatch-board", "document-expiry", "document-templates", "emergency-jobs", "files", "in-house",
  "invoices", "job-forms", "job-reopen-audit", "job-requests", "jobs", "knowledge-base", "live-team-map", "messages", "my-work", "notifications",
  "operative-scorecard", "pay", "platform", "portfolio-health", "predictive-maintenance", "projects", "quote", "quotes", "re-upload", "report-issue",
  "report-status", "reports", "schedule", "settings", "setup", "share", "signup", "slas", "staff-monitoring", "staff-performance", "supplier-analytics",
  "supplier-compare", "supplier-compliance", "supplier-onboarding", "supplier-outreach", "supplier-portal", "suppliers", "templates", "tenant-analytics",
  "tenant-report-forms", "timeline", "track", "vendor-messages", "vendor-onboard", "vendor-quote", "vendor-scorecard", "vendors", "verify-email", "whatsapp",
  "work-orders", "dashboard", "help", "properties", "team", "billing", "search"]);

export function getSlug() {
  try { return localStorage.getItem(KEY) || ""; } catch { return ""; }
}

export function setSlug(slug) {
  const s = (slug || "").toLowerCase();
  if (s === getSlug()) return;
  try { s ? localStorage.setItem(KEY, s) : localStorage.removeItem(KEY); } catch {}
  listeners.forEach((fn) => fn(s));
}

export function subscribeSlug(fn) { listeners.add(fn); return () => listeners.delete(fn); }

/* First path segment that isn't one of ours → a client's link (e.g. a bookmarked /apc/jobs opened before signing in) */
export function detectSlug(pathname) {
  const seg = (pathname || "").split("/")[1] || "";
  return SLUG_RE.test(seg) && !RESERVED_PATHS.has(seg) ? seg : "";
}

/* Browser URL → app route: /apc/jobs → /portal/jobs */
export function toInternal(pathname, slug) {
  if (!slug) return pathname;
  if (pathname === `/${slug}`) return "/portal";
  if (pathname.startsWith(`/${slug}/`)) return `/portal${pathname.slice(slug.length + 1)}`;
  return pathname;
}

/* App route → browser URL: /portal/jobs → /apc/jobs */
export function toExternal(pathname, slug) {
  if (!slug) return pathname;
  if (pathname === "/portal") return `/${slug}`;
  if (pathname.startsWith("/portal/")) return `/${slug}${pathname.slice(7)}`;
  return pathname;
}

export function mapTo(to, slug) {
  const p = typeof to === "string" ? parsePath(to) : { ...to };
  return { ...p, pathname: toExternal(p.pathname || "/", slug) };
}

export const loginPath = (slug = getSlug()) => (slug ? `/login/${slug}` : "/login");
